import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Fragment, useState, type ReactNode } from 'react';
import { errorText, useApi } from '../lib/api';
import { useRunScope } from '../lib/characters';
import { when } from '../lib/datetime';
import { OUTCOME_TONES, type Tone } from '../lib/story/types';
import {
  endAlly,
  playerAllies,
  playerLedger,
  playerMissions,
  playerWorldFlags,
  resetMission,
  resetWorldFlags,
  voidLedgerRow,
  type AllyRow,
  type LedgerRow,
  type MissionProgressRow,
  type WorldFlagRow,
} from '../lib/storyApi';
import { TONE_COLORS } from '../lib/story/ui';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';
const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';
const tab =
  'rounded border-2 border-ink px-3 py-1 text-sm font-semibold hover:bg-caption/50 aria-selected:bg-ink aria-selected:text-newsprint';
const plate = 'rounded px-1.5 py-0.5 font-semibold';

/** Status plates: cyan = live/counted/active, red = rejected/failed/lost, plain = history. */
const statusCls: Record<string, string> = {
  counted: 'bg-benday',
  active: 'bg-benday',
  scheduled: 'bg-benday',
  delivered: 'bg-nonphoto',
  done: 'bg-nonphoto',
  ready: 'bg-caption',
  in_encounter: 'bg-caption',
  rejected: 'bg-blam text-white',
  failed: 'bg-blam text-white',
  lost: 'bg-blam text-white',
};
export const Plate = ({ s }: { s: string | null | undefined }) =>
  s ? <span className={`${plate} ${statusCls[s] ?? 'bg-newsprint'}`}>{s}</span> : null;

const toneText: Partial<Record<Tone, string>> = { bad: 'text-white', disaster: 'text-white' };
export const TonePlate = ({ tone }: { tone: string }) =>
  (OUTCOME_TONES as readonly string[]).includes(tone) ? (
    <span
      className={`${plate} ${toneText[tone as Tone] ?? ''}`}
      style={{ background: TONE_COLORS[tone as Tone] }}
    >
      {tone}
    </span>
  ) : (
    <span className={`${plate} bg-newsprint`}>{tone || '-'}</span>
  );

const list = <T,>(d: unknown): T[] => (Array.isArray(d) ? (d as T[]) : []);
/** Run-scoped story keys (ADR 0069: `runId` = the selected character). */
const keyOf = (playerId: string, what: string, runId?: string) => [
  'admin-player-story',
  playerId,
  what,
  runId,
];

/** A 404 or a failed fetch means the endpoint isn't deployed yet (the S1-ENG/S1-SCN API), not a broken page. */
const notThereYet = (e: unknown) =>
  e instanceof TypeError || (e instanceof Error && /^HTTP 404\b|^Not Found\b/.test(e.message));

/** Loading, "not available yet", or an error line; otherwise the rows. */
function Loaded<T>({
  q,
  endpoint,
  empty,
  children,
}: {
  q: UseQueryResult<T[]>;
  endpoint: string;
  empty: string;
  children: (rows: T[]) => ReactNode;
}) {
  if (q.isPending) return <p className="text-sm">Loading&hellip;</p>;
  if (q.isError)
    return notThereYet(q.error) ? (
      <p className="text-sm text-bruise">
        Not available yet: this needs the S1-ENG/S1-SCN API (<code>{endpoint}</code>)
      </p>
    ) : (
      <p className="text-sm text-blam">{errorText(q.error)}</p>
    );
  if (q.data.length === 0) return <p className="text-sm">{empty}</p>;
  return <>{children(q.data)}</>;
}

type View = 'ledger' | 'allies' | 'flags' | 'missions';
const VIEWS: [View, string][] = [
  ['ledger', 'Ledger'],
  ['allies', 'Allies'],
  ['flags', 'World flags'],
  ['missions', 'Mission progress'],
];

/**
 * The player's story (engine.html §5, scenario.html §5): the choice ledger, the run's allies and deliveries, world flags and mission
 * progress, each loaded when its tab opens. Void, end, and reset take a reason and a `confirm()`, then refetch that tab. The
 * endpoints are stubs in `lib/storyApi.ts` until the API streams ship them; a 404 shows a note instead of an error.
 */
export function PlayerStorySection({ playerId }: { playerId: string }) {
  const [view, setView] = useState<View>('ledger');
  return (
    <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
      <div className="mb-2 flex flex-wrap items-center gap-2" role="tablist">
        <h2 className="mr-2 font-semibold">Story</h2>
        {VIEWS.map(([v, label]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            className={tab}
            onClick={() => setView(v)}
          >
            {label}
          </button>
        ))}
      </div>
      {view === 'ledger' && <LedgerTab playerId={playerId} />}
      {view === 'allies' && <AlliesTab playerId={playerId} />}
      {view === 'flags' && <FlagsTab playerId={playerId} />}
      {view === 'missions' && <MissionsTab playerId={playerId} />}
    </section>
  );
}

function LedgerTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: keyOf(playerId, 'ledger', runId),
    queryFn: async () =>
      list<LedgerRow>(await playerLedger(client, playerId)).sort(
        (a, b) => b.chosenAtMs - a.chosenAtMs || b.createdAt.localeCompare(a.createdAt),
      ),
  });
  const toggle = (k: string) => setOpen(open === k ? null : k);
  return (
    <Loaded q={q} endpoint="GET /admin/players/{id}/ledger" empty="No ledger rows.">
      {(rows) => (
        <table className="w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>When</th>
              <th className={th}>Chain</th>
              <th className={th}>Node</th>
              <th className={th}>Option</th>
              <th className={th}>Outcome</th>
              <th className={th}>Tone</th>
              <th className={th}>Tags</th>
              <th className={th}>Raid</th>
              <th className={th}>Status</th>
              <th className={th}>Windfall</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr
                  className={`border-t border-newsprint align-top ${r.status === 'voided' ? 'text-bruise line-through' : ''}`}
                >
                  <td className={`${td} font-mono`}>
                    {when(r.createdAt) || new Date(r.chosenAtMs).toLocaleString()}
                  </td>
                  <td className={`${td} font-mono`}>{r.chainId}</td>
                  <td className={`${td} font-mono`}>{r.nodeKey}</td>
                  <td className={`${td} font-mono`}>
                    {r.optionId}
                    {r.timedOut && <span className="ml-1 text-blam">(timed out)</span>}
                  </td>
                  <td className={`${td} font-mono`}>{r.outcomeId}</td>
                  <td className={td}>
                    <TonePlate tone={r.tone} />
                  </td>
                  <td className={`${td} font-mono`}>{(r.tags ?? []).join(', ')}</td>
                  <td className={`${td} font-mono`}>
                    {r.raidId ?? '-'}
                    {r.raidOutcome && <div className="text-bruise">{r.raidOutcome}</div>}
                  </td>
                  <td className={td}>
                    <Plate s={r.status} />
                  </td>
                  <td className={`${td} font-mono`}>{r.windfallValue || ''}</td>
                  <td className={`${td} whitespace-nowrap`}>
                    <button
                      type="button"
                      className={btn}
                      aria-expanded={open === `x:${r.id}`}
                      onClick={() => toggle(`x:${r.id}`)}
                    >
                      Details
                    </button>{' '}
                    {r.status === 'counted' && (
                      <button type="button" className={btn} onClick={() => toggle(`v:${r.id}`)}>
                        Void
                      </button>
                    )}
                  </td>
                </tr>
                {open === `x:${r.id}` && (
                  <tr className="bg-newsprint/40">
                    <td colSpan={11} className="px-2 py-2">
                      <div className="grid gap-2 md:grid-cols-3">
                        {(['truth', 'cast', 'facts'] as const).map((k) => (
                          <div key={k}>
                            <div className="font-semibold">{k}</div>
                            <pre className="overflow-x-auto font-mono text-[11px]">
                              {JSON.stringify(r[k] ?? null, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                      <div className="mt-1 text-bruise">
                        entry {r.entryId} · depth {r.depth} · content v{r.contentVersion} · run{' '}
                        {r.runId}
                      </div>
                    </td>
                  </tr>
                )}
                {open === `v:${r.id}` && (
                  <tr className="bg-newsprint/40">
                    <td colSpan={11} className="px-2 py-2">
                      <ReasonEditor
                        queryKey={keyOf(playerId, 'ledger', runId)}
                        label={`Void reason for ledger row ${r.id}`}
                        button="Void row"
                        note="Voided rows stop counting: conditions and echoes no longer read them."
                        confirmText={`Void ledger row ${r.chainId} / ${r.nodeKey} / ${r.optionId}?`}
                        send={(reason) => voidLedgerRow(client, r.id, reason)}
                        onDone={() => setOpen(null)}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Loaded>
  );
}

function AlliesTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: keyOf(playerId, 'allies', runId),
    queryFn: async () => list<AllyRow>(await playerAllies(client, playerId)),
  });
  return (
    <Loaded q={q} endpoint="GET /admin/players/{id}/allies" empty="No allies this run.">
      {(rows) => (
        <div className="space-y-3">
          {rows.map((a) => (
            <div key={a.id} className="rounded border border-newsprint p-2">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-semibold">{a.name}</span>
                <span className="font-mono text-bruise">{a.allyId}</span>
                <span>{a.disposition}</span>
                <Plate s={a.status} />
                <span className="font-mono text-bruise">
                  since {when(a.createdAt)}
                  {a.endedAt ? ` · ended ${when(a.endedAt)}` : ''}
                </span>
                {a.status === 'active' && (
                  <button
                    type="button"
                    className={`${btn} ml-auto`}
                    onClick={() => setOpen(open === a.id ? null : a.id)}
                  >
                    End ally
                  </button>
                )}
              </div>
              {open === a.id && (
                <div className="mt-2 bg-newsprint/40 p-2">
                  <ReasonEditor
                    queryKey={keyOf(playerId, 'allies', runId)}
                    label={`End reason for ${a.name}`}
                    button="End ally"
                    note="Cancels the scheduled deliveries; delivered crates stay."
                    confirmText={`End ally "${a.name}"?`}
                    send={(reason) => endAlly(client, a.id, reason)}
                    onDone={() => setOpen(null)}
                  />
                </div>
              )}
              {(a.deliveries ?? []).length === 0 ? (
                <p className="mt-1 text-xs text-bruise">No deliveries.</p>
              ) : (
                <table className="mt-1 w-full text-xs">
                  <thead className="text-bruise">
                    <tr>
                      <th className={th}>#</th>
                      <th className={th}>Items</th>
                      <th className={th}>Value</th>
                      <th className={th}>Arrives</th>
                      <th className={th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.deliveries.map((d) => (
                      <tr key={d.id} className="border-t border-newsprint">
                        <td className={`${td} font-mono`}>{d.seq}</td>
                        <td className={`${td} font-mono`}>
                          {(d.items ?? []).map((s) => `${s.itemId}×${s.quantity}`).join(', ') ||
                            '-'}
                        </td>
                        <td className={`${td} font-mono`}>{d.value}</td>
                        <td className={`${td} font-mono`}>{when(d.arrivesAt)}</td>
                        <td className={td}>
                          <Plate s={d.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </Loaded>
  );
}

function FlagsTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: keyOf(playerId, 'flags', runId),
    queryFn: async () => list<WorldFlagRow>(await playerWorldFlags(client, playerId)),
  });
  const key = keyOf(playerId, 'flags', runId);
  return (
    <Loaded q={q} endpoint="GET /admin/players/{id}/world-flags" empty="No world flags.">
      {(rows) => (
        <>
          <div className="mb-1 flex justify-end">
            <button
              type="button"
              className={btn}
              onClick={() => setOpen(open === '*run' ? null : '*run')}
            >
              Reset all run flags
            </button>
          </div>
          {open === '*run' && (
            <div className="mb-2 bg-newsprint/40 p-2">
              <ReasonEditor
                queryKey={key}
                label="Reset reason for all run flags"
                button="Reset run flags"
                note="Clears every run-scoped flag; account flags stay."
                confirmText="Reset all of this run's world flags?"
                send={(reason) => resetWorldFlags(client, playerId, { reason, scope: 'run' })}
                onDone={() => setOpen(null)}
              />
            </div>
          )}
          <table className="w-full text-xs">
            <thead className="text-bruise">
              <tr>
                <th className={th}>Scope</th>
                <th className={th}>Key</th>
                <th className={th}>Value</th>
                <th className={th}>Building</th>
                <th className={th}>Source raid</th>
                <th className={th}>Set</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const k = `${f.scopeKey}|${f.key}`;
                return (
                  <Fragment key={k}>
                    <tr className="border-t border-newsprint align-top">
                      <td className={`${td} font-mono`} title={f.scopeKey}>
                        {f.scope}
                      </td>
                      <td className={`${td} font-mono`}>{f.key}</td>
                      <td className={`${td} font-mono`}>{f.value}</td>
                      <td className={`${td} font-mono`}>{f.buildingId ?? '-'}</td>
                      <td className={`${td} font-mono`}>{f.sourceRaidId ?? '-'}</td>
                      <td className={`${td} font-mono`}>{when(f.setAt)}</td>
                      <td className={td}>
                        <button
                          type="button"
                          className={btn}
                          onClick={() => setOpen(open === k ? null : k)}
                        >
                          Reset
                        </button>
                      </td>
                    </tr>
                    {open === k && (
                      <tr className="bg-newsprint/40">
                        <td colSpan={7} className="px-2 py-2">
                          <ReasonEditor
                            queryKey={key}
                            label={`Reset reason for flag ${f.key}`}
                            button="Reset flag"
                            note="Clears the flag; the next raid sees it unset."
                            confirmText={`Reset world flag ${f.key}?`}
                            send={(reason) =>
                              resetWorldFlags(client, playerId, { reason, key: f.key })
                            }
                            onDone={() => setOpen(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </Loaded>
  );
}

function MissionsTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: keyOf(playerId, 'missions', runId),
    queryFn: async () => list<MissionProgressRow>(await playerMissions(client, playerId)),
  });
  const toggle = (k: string) => setOpen(open === k ? null : k);
  return (
    <Loaded q={q} endpoint="GET /admin/players/{id}/missions" empty="No mission progress.">
      {(rows) => (
        <table className="w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>Mission</th>
              <th className={th}>Status</th>
              <th className={th}>Stage</th>
              <th className={th}>Stage state</th>
              <th className={th}>Target</th>
              <th className={th}>Attempts</th>
              <th className={th}>Dates</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <Fragment key={m.missionId}>
                <tr className="border-t border-newsprint align-top">
                  <td className={`${td} font-mono`}>{m.missionId}</td>
                  <td className={td}>
                    <Plate s={m.status} />
                  </td>
                  <td className={`${td} font-mono`}>{m.stageId ?? '-'}</td>
                  <td className={td}>
                    <Plate s={m.stageState} />
                  </td>
                  <td className={`${td} font-mono`}>
                    {m.targetBuildingId != null && <div>building {m.targetBuildingId}</div>}
                    {m.targetTeamId && <div>team {m.targetTeamId}</div>}
                    {m.targetBuildingId == null && !m.targetTeamId && '-'}
                  </td>
                  <td className={`${td} font-mono`}>{m.attempts}</td>
                  <td className={`${td} font-mono`}>
                    {m.stageStartedAt && <div>stage {when(m.stageStartedAt)}</div>}
                    {m.repickAt && <div>repick {when(m.repickAt)}</div>}
                  </td>
                  <td className={`${td} whitespace-nowrap`}>
                    <button
                      type="button"
                      className={btn}
                      aria-expanded={open === `l:${m.missionId}`}
                      onClick={() => toggle(`l:${m.missionId}`)}
                    >
                      Log ({(m.log ?? []).length})
                    </button>{' '}
                    <button
                      type="button"
                      className={btn}
                      onClick={() => toggle(`r:${m.missionId}`)}
                    >
                      Reset
                    </button>
                  </td>
                </tr>
                {open === `l:${m.missionId}` && (
                  <tr className="bg-newsprint/40">
                    <td colSpan={8} className="px-2 py-2">
                      {(m.log ?? []).length === 0 ? (
                        <p>No stage log.</p>
                      ) : (
                        <ul className="font-mono">
                          {m.log.map((l, i) => (
                            <li key={i}>
                              {when(l.at)} · {l.stageId} · {l.verdict}
                              {l.raidId != null ? ` · raid ${l.raidId}` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
                {open === `r:${m.missionId}` && (
                  <tr className="bg-newsprint/40">
                    <td colSpan={8} className="px-2 py-2">
                      <ReasonEditor
                        queryKey={keyOf(playerId, 'missions', runId)}
                        label={`Reset reason for mission ${m.missionId}`}
                        button="Reset mission"
                        note="Starts the mission over from its first stage."
                        confirmText={`Reset mission ${m.missionId}?`}
                        send={(reason) => resetMission(client, playerId, m.missionId, reason)}
                        onDone={() => setOpen(null)}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Loaded>
  );
}

/** A reason + confirm + POST form (the `PlayerBasesSection` pattern). Refetches `queryKey` on success. */
export function ReasonEditor({
  queryKey,
  label,
  button,
  note,
  confirmText,
  send,
  onDone,
}: {
  queryKey: unknown[];
  label: string;
  button: string;
  note: string;
  confirmText: string;
  send: (reason: string) => Promise<unknown>;
  onDone: () => void;
}) {
  const { session } = useApi();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => send(reason.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
      onDone();
    },
    onError: (e) => setProblem(errorText(e)),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reason.trim()) return setProblem('A reason is required.');
        if (!confirm(confirmText)) return;
        run.mutate();
      }}
      className="flex flex-wrap items-center gap-2 text-sm"
    >
      {!session.isAdmin && <span className="text-xs text-bruise">This is for Admins.</span>}
      <input
        aria-label={label}
        placeholder="reason (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-80 max-w-full rounded border-2 border-ink bg-white px-2 py-1"
      />
      <button
        type="submit"
        disabled={run.isPending}
        className="rounded border-2 border-ink bg-blam px-3 py-1 text-sm font-semibold text-white disabled:opacity-50"
      >
        {button}
      </button>
      <span className="text-xs text-bruise">{note}</span>
      {problem && (
        <p className="w-full text-blam" role="alert">
          {problem}
        </p>
      )}
    </form>
  );
}
